<template>
    <div class="card">
        <div class="flex justify-content-between">
            <h5>{{ label('Task') }} Progress and Priority Reports</h5>
            <div class="flex justify-content-start gap-2">
                <Button :label="isLoading ? 'Printing Report...' : 'Print Report'" class="p-button-warning" @click="printReport" :loading="isLoading" />

                <a :href="downloadLink" target="_blank" class="text-white"><Button v-if="downloadLink && !isLoading" label="Download Report" /></a>
            </div>
        </div>
        <task-report-criteria />

        <!-- we can add notification component here later -->
        <div v-if="loading">
            <task-report-loader />
        </div>

        <div v-else>
            <ResultBar :message="reportMessage" :content="getFormattedTaskReportCriteria" />
            <task-status-priority-report />

            <ams-base-chart chartTitle="Primary Theme" :chartType="ChartTypes.BAR" :data="primaryThemeJson" :showLegend="false" />
            <ams-base-chart chartTitle="Secondary Theme" :chartType="ChartTypes.BAR" :data="secondaryThemeJson" :showLegend="false" />
            <ams-base-chart chartTitle="Level 3 Theme" :chartType="ChartTypes.BAR" :data="tertiaryThemeJson"  :showLegend="false"/>
            <ams-base-chart chartTitle="Level 4 Theme" :chartType="ChartTypes.BAR" :data="quaternaryThemeJson" :showLegend="false"/>


            <TaskByUsers :json-data="taskUsers"/>
            <task-report-table />
        </div>
    </div>
</template>
<script setup>
import ResultBar from '../modules/ResultBar.vue';
import TaskReportLoader from '../modules/TaskReportLoader.vue';
import TaskReportCriteria from '../modules/TaskReportCriteria.vue';
import TaskStatusPriorityReport from '../modules/TaskStatusPriorityReport.vue';
import TaskReportTable from '../modules/TaskReportTable.vue';

import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { authStore, reportStore, sourceStore, departmentStore, themeStore, userStore } from '../../../../../store/global-stores';
import { ChartTypes } from '../../../../../enums';
import { toCapitalize, removeHtmlTagsFromParagraph } from '../../../../../utilities';
import { Colors } from '../../../../../utilities/colors';

import { getAllProgress, getAllPriorities } from '../../../constants';
import { formatDate } from '../../../../../utilities/date-formatter';
import { showToast } from '../../../../../utilities/toast-handler';
import TaskByUsers from '@/product/ams/modules/report/modules/debrief-reports/TaskByUsers.vue';
import { useLabels } from '@/composables/LabelComposable';

const { loading, totalTaskRecords, dynamicTask, taskCriteria } = storeToRefs(reportStore);
const { flatSources } = storeToRefs(sourceStore);
const { flatDepartments } = storeToRefs(departmentStore);
const { flatThemeData } = storeToRefs(themeStore);
const { users } = storeToRefs(userStore);

const isLoading = ref(false);
const downloadLink = ref(null);
const { label } = useLabels();

const taskUsers = computed(() => {
    if (
        typeof dynamicTask === 'undefined' ||
        typeof dynamicTask.value === 'undefined' ||
        dynamicTask.value === null ||
        typeof dynamicTask.value.taskStatus === 'undefined'
    ) {
        return []
    } else {
        return dynamicTask.value.taskStatus.map( (data) => {

            return {
                label: `${data.name} ${data.surname}`,
                values: [
                    {
                        label: 'Complete',
                        value: data.Complete,
                        bgcolor: Colors.GREEN,
                    },
                    {
                        label: 'In Progress',
                        value: data.In_Progress,
                        bgcolor: Colors.BLUE,
                    },
                    {
                        label: 'No Progress',
                        value: data.No_Progress,
                        bgcolor: Colors.RED,
                    },
                    {
                        label: 'QA Archive',
                        value: data.QA_Archive,
                        bgcolor: Colors.TEAL,
                    },
                ]
            }
        } )
    }
})

const primaryThemeJson = computed(() => {

    return dynamicTask.value?.primaryTheme?.map((dataObj) => {
        return {
            label: toCapitalize(dataObj.name),
            values: [
                {
                    value: dataObj.tot,
                    bgcolor: Colors.BLUE,
                },
            ],
        };
    });
});

const secondaryThemeJson = computed(() => {
    return dynamicTask.value?.secondaryTheme?.map((dataObj) => {
        return {
            label: toCapitalize(dataObj.name),
            values: [
                {
                    value: dataObj.tot,
                    bgcolor: Colors.BLUE,
                },
            ],
        };
    });
});

const tertiaryThemeJson = computed(() => {
    return dynamicTask.value?.level3Theme?.map(item => ({
        label: toCapitalize(item.name),
        values: [{ value: item.tot, bgcolor: Colors.BLUE }]
    }));
});

const quaternaryThemeJson = computed(() => {
    return dynamicTask.value?.level4Theme?.map(item => ({
        label: toCapitalize(item.name),
        values: [{ value: item.tot, bgcolor: Colors.BLUE }]
    }));
});


const reportMessage = computed(() => {
    return totalTaskRecords.value
        ? `${totalTaskRecords.value} ${label('Tasks').toLowerCase()}`
        : `no any ${label('Tasks').toLowerCase()}`;
});

// For the banner result section of selected criterias
const getFormattedTaskReportCriteria = computed(() => {
    const activeCriterias = {
        Title: taskCriteria.value.title ?? null,
        Description: taskCriteria.value.description ?? null,
        Status: getExistingEntities('status') ?? null,
        Priorities: getExistingEntities('priority') ?? null,
        Sources: getExistingEntities('source') ?? null,
        Themes: getExistingEntities('theme') ?? null,
        'Incident No': taskCriteria.value.incident_number ?? null,
        'From Departments': getExistingEntities('from_department') ?? null,
        'To Departments': getExistingEntities('to_department') ?? null,
        'Start Date From': taskCriteria.value.start_date_from ?? null,
        'Start Date To': taskCriteria.value.start_date_to ?? null,
        'Due Date From': taskCriteria.value.due_date_from ?? null,
        'Due Date To': taskCriteria.value.due_date_to ?? null,
        'Assigned To': getExistingEntities('users_assigned') ?? null,
    };
    const filteredObject = Object.fromEntries(Object.entries(activeCriterias).filter(([key, value]) => key !== null && value !== null));
    return filteredObject;
});

const getEntitiesBasedOnTitle = (entity) => {
    switch (entity) {
        case 'status':
            return getAllProgress();

        case 'priority':
            return getAllPriorities();

        case 'source':
            return flatSources.value;

        case 'from_department':
        case 'to_department':
            return flatDepartments.value;

        case 'theme':
            return flatThemeData.value;

        case 'users_assigned':
            return users.value;
    }
};

const getCriteriaEntryBasedOnEntity = (entity) => {
    switch (entity) {
        case 'status':
            return 'status';
        case 'priority':
            return 'priority';
        case 'source':
            return 'source_id';
        case 'from_department':
            return 'from_department_id';
        case 'to_department':
            return 'to_department_id';
        case 'theme':
            return 'theme_id';
        case 'users_assigned':
            return 'assigned_to_id';
    }
};

const getExistingEntities = (entity) => {
    let entityString = null;
    const criteriaEntry = getCriteriaEntryBasedOnEntity(entity);
    const existingCriteria = taskCriteria.value[criteriaEntry];
    if (existingCriteria.length > 0) {
        const allEntities = getEntitiesBasedOnTitle(entity);
        entityString = existingCriteria
            ?.map((entityId) => {
                const currentEntity = allEntities?.find((entity) => entity.id === entityId);
                return (entity === 'users_assigned' ? currentEntity?.fullname : currentEntity?.name) ?? null;
            })
            ?.join(', ');
    }
    return entityString;
};

const printReport = async () => {
    try {
        isLoading.value = true;
        // 1: fetch template response from the api
        const templateResponse = await reportStore.generateTaskTemplateResponse();

        //console.log('templateResponse', templateResponse)

        // 2: Format template payload
        const templatePayloadJson = formatTemplateResponse(templateResponse.data);
        console.log('Template payload', templatePayloadJson);
        // 3: Print report
        downloadLink.value = await reportStore.downloadReport(templatePayloadJson, 'task');
    } catch (error) {
        showToast({}, 'Error while printing the report');
    } finally {
        // console.log(downloadLink);
        isLoading.value = false;
    }
};

const formatTemplateResponse = (templateResponse) => {
    //  ========> Task status/progress
    const progressData = templateResponse.progress;
    const progress = progressData?.map((progressItem) => {
        return {
            id: progressItem.progress,
            name: progressItem.name,
            tot: progressItem.tot,
            percentage: progressItem.percentage?.toFixed(2),
        };
    });

    // ========> Task status pie format
    const progressColors = ['#ff6384', '#ffcd56', '#65AC65', '#61dccd'];
    const progressPie = {
        data: [],
        labels: [],
        color: [],
    };

    progressData.forEach((item) => {
        progressPie.data.push(item.tot);
        progressPie.labels.push(item.name);
        const progressColor = progressColors[parseInt(item.progress) - 1];
        progressPie.color.push(progressColor);
    });

    //  ========> Task priority
    const priorityData = templateResponse.priority;
    const priority = priorityData?.map((priorityItem) => {
        return {
            id: priorityItem.priority,
            name: priorityItem.name,
            tot: priorityItem.tot,
            percentage: priorityItem.percentage?.toFixed(2),
        };
    });

    //  ========> Task status pie format
    const priorityColors = ['#36a2eb', '#ffcd56', '#ff6384'];
    const priorityPie = {
        data: [],
        labels: [],
        color: [],
    };

    priorityData.forEach((item) => {
        priorityPie.data.push(item.tot);
        priorityPie.labels.push(item.name);
        const priorityColor = priorityColors[parseInt(item.priority) - 1];
        priorityPie.color.push(priorityColor);
    });

    //  ========> Primary theme bar data
    const primaryThemeData = templateResponse.primaryTheme;
    const primaryThemeBar = {
        data: [],
        labels: [],
    };

    primaryThemeData.forEach((item) => {
        primaryThemeBar.data.push(item.tot), primaryThemeBar.labels.push(item.name);
    });

    // ========>  Secondary theme bar data
    const secondaryThemeData = templateResponse.secondaryTheme;
    const secondaryThemeBar = {
        data: [],
        labels: [],
        fillColors: ['#fff'],
    };

    secondaryThemeData.forEach((item) => {
        secondaryThemeBar.data.push(item.tot), secondaryThemeBar.labels.push(item.name);
    });
    
    // ========>  Level 3 theme bar data
    const level3ThemeData = templateResponse.level3Theme;
    const level3ThemeBar = {
        data: [],
        labels: [],
        fillColors: ['#fff'],
    };

    level3ThemeData.forEach((item) => {
        level3ThemeBar.data.push(item.tot), level3ThemeBar.labels.push(item.name);
    });
    
    // ========>  Level 4 theme bar data
    const level4ThemeData = templateResponse.level4Theme;
    const level4ThemeBar = {
        data: [],
        labels: [],
        fillColors: ['#fff'],
    };

    level4ThemeData.forEach((item) => {
        level4ThemeBar.data.push(item.tot), level4ThemeBar.labels.push(item.name);
    });
    // ========> Table data

    const rawData = templateResponse.raw;
    const allProgress = getAllProgress();
    const allPriorities = getAllPriorities();

    const tableData = rawData.map((item) => {
        return {
            id: item.id,
            title: item.title,
            description: removeHtmlTagsFromParagraph(item.description),
            theme: item.theme?.name,
            department_to: item.department_to?.name,
            status: allProgress.find((progress) => progress.id === parseInt(item.progress))?.name,
            priority: allPriorities.find((priority) => priority.id == parseInt(item.priority))?.name,
            updates: item.comments?.map((commentData) => {
                return removeHtmlTagsFromParagraph(commentData.comment);
            }),
            createdAt: formatDate(item.created_at),
        };
    });


    const taskStatus = templateResponse.taskStatus.map((item) => {
        return {
            email: item.email,
            name: item.name,
            surname: item.surname,
            No_Progress: item.No_Progress,
            In_Progress: item.In_Progress,
            Complete: item.Complete,
            QA_Archive: item.QA_Archive,
        }
    })

    //const taskStatus = templateResponse.taskStatus
    console.log('taskStatus', taskStatus)

    const templatePayloadJson = {
        author: authStore.getLoggedUserName, //  ========> Setup author and generated date,
        taskCount: templateResponse.raw?.length, //  ========> Count tasks in raw data
        // taskCriteria: getFormattedTaskReportCriteria.value, //  ========> Format Task Criterias => Not needed for now, uncomment it when needed
        progress,
        priority,
        progressPie,
        priorityPie,
        primaryThemeBar,
        secondaryThemeBar,
        level3ThemeBar,
        level4ThemeBar,
        tableData,
        taskStatus
    };
    return templatePayloadJson;
};
</script>
